import { Link, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  BookText,
  DoorOpen,
  Grid3x3,
  Orbit,
  Workflow,
  Bell,
  CalendarCheck,
  CalendarCog,
  Crosshair,
  Filter,
  HandHeart,
  LayoutDashboard,
  ListChecks,
  type LucideIcon,
  UserCheck,
  Megaphone,
  MousePointerClick,
  Network,
  PhoneCall,
  PiggyBank,
  Route,
  Settings,
  Target,
  TrendingUp,
  X,
  PenLine,
} from 'lucide-react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { UserMenu } from './UserMenu';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path?: string; // undefined → built later (shown, inert)
}

/**
 * Left nav. On md+ it's a static 240px column (in flow). Below md it's an
 * off-canvas drawer (fixed, slides in over a backdrop) — `open`/`onClose` are
 * driven by the FilterBar hamburger in AppShell; tapping a link closes it.
 */
export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const groups: { heading: string; items: NavItem[] }[] = [
    {
      heading: t.navAnalyse(),
      items: [
        { icon: Activity, label: t.navTruth(), path: '/sannheten' },
        { icon: LayoutDashboard, label: t.navOverview(), path: '/' },
        { icon: Filter, label: t.navFunnel(), path: '/funnel' },
        { icon: TrendingUp, label: t.navTrends(), path: '/trends' },
        { icon: Megaphone, label: t.navAds(), path: '/ads' },
        { icon: MousePointerClick, label: t.navLanding(), path: '/landing' },
        { icon: Route, label: t.navAttribution(), path: '/attribution' },
        { icon: ListChecks, label: t.navQuiz(), path: '/quiz' },
        { icon: CalendarCheck, label: t.navCalendar(), path: '/kalender' },
        { icon: PhoneCall, label: t.navLeads(), path: '/leads' },
        { icon: HandHeart, label: t.navReferrals(), path: '/verving' },
      ],
    },
    {
      heading: t.navMeta(),
      items: [
        { icon: Orbit, label: t.navMetaAccount(), path: '/meta' },
        { icon: UserCheck, label: t.navMetaCustomers(), path: '/meta/kunder' },
        { icon: Grid3x3, label: t.navMetaProduction(), path: '/meta/produksjon' },
        { icon: Workflow, label: t.navMetaSystem(), path: '/meta/system' },
        { icon: DoorOpen, label: t.navMetaDoors(), path: '/meta/dorer' },
        { icon: BookText, label: t.navMetaHistory(), path: '/meta/historikk' },
      ],
    },
    {
      heading: t.navAction(),
      items: [
        { icon: Network, label: t.navMap(), path: '/kart' },
        { icon: PenLine, label: t.navContent(), path: '/innhold' },
        { icon: Bell, label: t.navAlerts(), path: '/alerts' },
        { icon: Target, label: t.navPlan(), path: '/plan' },
        { icon: PiggyBank, label: t.navProfitability(), path: '/lonnsomhet' },
      ],
    },
    {
      heading: t.navBooking(),
      items: [
        { icon: CalendarCheck, label: t.navBookings(), path: '/bookinger' },
        { icon: CalendarCog, label: t.navAvailability(), path: '/tilgjengelighet' },
      ],
    },
    {
      heading: t.navSettings(),
      items: [
        { icon: Crosshair, label: t.navTargets(), path: '/targets' },
        { icon: Settings, label: t.navRules(), path: '/rules' },
      ],
    },
  ];

  return (
    <>
      {/* Mobile drawer backdrop — tap to close. Inert on md+. */}
      <button
        type="button"
        aria-label={t.navCloseMenu()}
        tabIndex={open ? 0 : -1}
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-[var(--overlay)] backdrop-blur-sm transition-opacity duration-200 md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-full w-60 shrink-0 flex-col border-r border-border bg-surface',
          'transition-transform duration-200 ease-out md:static md:z-auto md:translate-x-0 md:transition-none',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* brand lockup */}
        <div className="flex h-14 items-center gap-2.5 px-5">
          <div className="flex size-7 items-center justify-center rounded-md bg-brand text-brand-fg">
            <span className="text-label font-bold">N</span>
          </div>
          <div className="leading-none">
            <div className="text-section text-fg">{t.appName()}</div>
            <div className="mt-0.5 text-micro text-fg-subtle">{t.appTagline()}</div>
          </div>
          {/* close (mobile drawer only) */}
          <button
            type="button"
            onClick={onClose}
            aria-label={t.navCloseMenu()}
            className="ml-auto flex size-9 items-center justify-center rounded-md text-fg-subtle transition-fast hover:bg-surface-2 hover:text-fg md:hidden"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {groups.map((group) => (
            <div key={group.heading} className="mb-4">
              <div className="px-2 pb-1.5 text-micro font-semibold tracking-wider text-fg-subtle">
                {group.heading}
              </div>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <li key={item.label}>
                    <NavRow item={item} active={item.path === pathname} onNavigate={onClose} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <UserMenu />
        </div>
      </aside>
    </>
  );
}

function NavRow({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate: () => void;
}) {
  const Icon = item.icon;
  // Taller tap target in the mobile drawer (>=40px); dense on md+ desktop.
  const base =
    'flex items-center gap-2.5 rounded-md border-l-2 border-transparent py-2.5 pl-1.5 pr-2 text-body transition-fast md:py-1.5';
  const content = (
    <>
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
    </>
  );

  if (!item.path) {
    // Shown so the full IA reads correctly, but not yet built.
    return (
      <span className={cn(base, 'cursor-default text-fg-subtle/60')} aria-disabled>
        {content}
      </span>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      className={cn(
        base,
        active
          ? 'border-brand bg-brand-tint font-medium text-brand'
          : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
      )}
    >
      {content}
    </Link>
  );
}
